import type { ComboBoxStateOptions } from '@react-stately/combobox';
import type { ElementType } from 'react';
import type { VariantProps } from 'tailwind-variants';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useButton } from '@react-aria/button';
import { useComboBox } from '@react-aria/combobox';
import { useFocusRing } from '@react-aria/focus';
import { useFilter } from '@react-aria/i18n';
import { useHover } from '@react-aria/interactions';
import { mergeProps, useLayoutEffect, useResizeObserver } from '@react-aria/utils';
import { useComboBoxState } from '@react-stately/combobox';

import type { InternalComponentProps, OverrideProps } from '../../types';
import type { BaseComboboxProps } from './BaseComboboxProps';
import { createComponent, mergeRefs } from '../../utils';
import { dimensionValue } from '../../utils/dimensionsValue';
import { FormControl } from '../FormControl';
import { ListBoxBase, useListBoxLayout } from '../ListBoxBase';
import { Popover } from '../Popover';
import { comboboxVariants } from './comboboxVariants';

export interface SingleComboboxTypeMap<
  AdditionalProps = {},
  DefaultComponent extends React.ElementType = 'div',
> {
  props: AdditionalProps &
    ComboBoxStateOptions<object> &
    VariantProps<typeof comboboxVariants> &
    BaseComboboxProps & {};
  defaultComponent: DefaultComponent;
}

export type SingleComboboxProps<
  RootComponent extends ElementType = SingleComboboxTypeMap['defaultComponent'],
  AdditionalProps = {},
> = OverrideProps<SingleComboboxTypeMap<AdditionalProps, RootComponent>, RootComponent>;

type InternalSingleComboboxProps<
  RootComponent extends ElementType = SingleComboboxTypeMap['defaultComponent'],
  AdditionalProps = {},
> = InternalComponentProps<SingleComboboxTypeMap<AdditionalProps, RootComponent>>;

export const SingleCombobox = createComponent<SingleComboboxTypeMap>(
  <BaseComponentType extends React.ElementType = SingleComboboxTypeMap['defaultComponent']>(
    inProps: InternalSingleComboboxProps<BaseComponentType>,
  ) => {
    const {
      as: Component = 'div',
      hideDropdownButton = false,
      label,
      noResult,
      isLoading,
      loadingText,
      helperText,
      helperTextProps: helperTextPropsProp,
      labelProps: labelPropsProp,
      isInvalid,
      isDisabled,
      autoFocus,
      className,
      maxHeight = 320,
      offset = 8,
      placement = 'bottom start',
      menuWidth: customMenuWidth,
      size = 'sm',
      isFullWidth,
      loadingState,
      errorMessage,
      name,
      startContent,
      onLoadMore,
      isOpen,
      ref,
      isNotEditable = false,
      inputName,
    } = inProps;

    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listBoxRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const { contains } = useFilter({ sensitivity: 'base' });

    const layout = useListBoxLayout(size);
    const state = useComboBoxState({
      ...inProps,
      // allow menu to open with no results if the user has set an
      // empty state or the combobox is indicated to be loading.
      // This enables us to open the menu and show these two states.
      allowsEmptyCollection: noResult !== undefined || isLoading,
      defaultFilter: contains,
      menuTrigger: 'focus',
    });

    useEffect(() => {
      if (isOpen) {
        state.open();
      }
    }, [state, isOpen]);

    const {
      buttonProps: triggerProps,
      inputProps,
      listBoxProps,
      labelProps,
      descriptionProps,
      errorMessageProps,
    } = useComboBox(
      {
        ...inProps,
        buttonRef,
        inputRef,
        listBoxRef,
        popoverRef,
      },
      state,
    );

    const { buttonProps } = useButton(triggerProps, buttonRef);
    const { hoverProps, isHovered } = useHover({ isDisabled });
    const { isFocusVisible, isFocused, focusProps } = useFocusRing({
      autoFocus,
      isTextInput: true,
      within: true,
    });

    const styles = useMemo(
      () =>
        comboboxVariants({
          className,
          isDisabled,
          isFullWidth,
          size,
          isNotEditable,
        }),
      [className, isDisabled, isFullWidth, size, isNotEditable],
    );

    // Measure the width of the inputfield and the button to inform the width of the menu (below).
    const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

    const onResize = useCallback(() => {
      if (containerRef.current) {
        const buttonWidth = containerRef.current.offsetWidth;
        setMenuWidth(buttonWidth);
      }
    }, []);

    useResizeObserver({
      ref: containerRef,
      onResize,
    });

    useLayoutEffect(onResize, [onResize]);

    const style = {
      width: customMenuWidth ? dimensionValue(customMenuWidth) : menuWidth,
      minWidth: menuWidth,
    };

    return (
      <FormControl
        className={styles.formControl({ className })}
        errorMessage={errorMessage}
        helperText={helperText}
        helperTextProps={mergeProps(descriptionProps, errorMessageProps, helperTextPropsProp)}
        isFullWidth={isFullWidth}
        isInvalid={isInvalid}
        label={label}
        labelProps={mergeProps(labelProps, labelPropsProp)}
      >
        <Component
          {...mergeProps(hoverProps, focusProps)}
          className={styles.base({ className })}
          data-disabled={isDisabled}
          data-focus={isFocused}
          data-focus-visible={isFocusVisible}
          data-hover={isHovered}
          data-invalid={isInvalid}
          data-open={state.isOpen}
          name={name}
          ref={mergeRefs(containerRef, ref)}
        >
          {startContent}
          <input
            {...inputProps}
            className={styles.input()}
            disabled={isNotEditable || inputProps.disabled}
            ref={inputRef}
          />
          {!hideDropdownButton && (
            <button
              {...buttonProps}
              name={inputName ?? inputProps.name}
              className={styles.button()}
              ref={buttonRef}
              type="button"
            >
              <ChevronDownIcon className={styles.icon()} />
            </button>
          )}
          {state.isOpen ? (
            <Popover
              className={styles.popover()}
              isNonModal
              maxHeight={maxHeight}
              offset={offset}
              placement={placement}
              ref={popoverRef}
              scrollRef={listBoxRef}
              state={state}
              style={style}
              triggerRef={containerRef}
            >
              <ListBoxBase
                {...listBoxProps}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={state.focusStrategy ?? undefined}
                className={styles.listBox()}
                disallowEmptySelection
                focusOnPointerEnter
                isLoading={loadingState === 'loading' || loadingState === 'loadingMore'}
                layout={layout}
                loadingContent={loadingText}
                noResultContent={noResult}
                onLoadMore={onLoadMore}
                ref={listBoxRef}
                shouldSelectOnPressUp
                shouldUseVirtualFocus
                showLoadingSpinner={loadingState === 'loadingMore'}
                size={size}
                state={state}
              />
            </Popover>
          ) : null}
        </Component>
      </FormControl>
    );
  },
);

SingleCombobox.displayName = 'SingleCombobox';
